import React, { useEffect, useMemo, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { pageStyles } from '@/components/utils/PageLayout';
import { PageFrame, PageHeader, Surface, Button, Note } from '@/components/ui';
import { ordersApi } from '@/services/api';
import { formatDate, formatNumber } from '@/utils/formatters';
import { sumBy } from '@/utils/aggregate';

const BUCKETS = ['0-1', '1-3', '3-7', '7-12', '12-20', '20-40', '40-70', '70-100'] as const;
type Bucket = typeof BUCKETS[number];

interface SchemeCashBucket {
  scheme_id: number;
  scheme_name: string;
  display_name?: string;
  full_name?: string;
  acronym?: string;
  aggr_pct: number;
  buckets: Record<Bucket, number>;
}

interface SchemeCashData {
  holding_date: string;
  instant_cash: SchemeCashBucket[];
  cash_plus_liquid: SchemeCashBucket[];
}

/** The scheme's full name, shown on hover of whichever short form is in the cell. */
const withFullName = (row: SchemeCashBucket, text: React.ReactNode) => (
  <Tooltip
    title={row.full_name || ''}
    placement="top"
    arrow
    disableHoverListener={!row.full_name}
    disableTouchListener={!row.full_name}
    disableFocusListener={!row.full_name}
  >
    <span style={{ cursor: row.full_name ? 'help' : 'default' }}>{text}</span>
  </Tooltip>
);

const columns: ColumnDef<SchemeCashBucket>[] = [
  {
    key: 'scheme_name',
    header: 'Scheme',
    sortable: true,
    render: row => withFullName(row, <span className={pageStyles['cell-strong']}>{row.scheme_name}</span>),
    renderFooter: () => 'Totals',
  },
  {
    key: 'display_name',
    header: 'Display Name',
    sortable: true,
    render: row => withFullName(row, row.display_name || '—'),
    renderFooter: rows => (
      <span className={pageStyles['cell-sub']}>
        {rows.length} scheme{rows.length === 1 ? '' : 's'}
      </span>
    ),
  },
  {
    key: 'aggr_pct',
    header: 'Aggr. (%)',
    align: 'right',
    sortable: true,
    render: row => formatNumber(row.aggr_pct, 2),
    // An aggregate percentage of AUM — a column of shares does not add up, so
    // this cell stays empty while the bucket counts beside it total.
    renderFooter: () => null,
  },
  ...BUCKETS.map((bucket): ColumnDef<SchemeCashBucket> => ({
    key: bucket,
    header: `${bucket}%`,
    align: 'right',
    sortable: true,
    render: row => formatNumber(row.buckets?.[bucket] ?? 0, 0),
    renderFooter: rows => formatNumber(sumBy(rows, r => r.buckets?.[bucket]), 0),
  })),
];

interface BucketTableProps {
  rows: SchemeCashBucket[];
  loading: boolean;
  error: string | null;
}

/**
 * One of the page's two cash views. Each keeps its own sort so ordering one
 * table does not reorder the other — they are read side by side.
 */
const BucketTable: React.FC<BucketTableProps> = ({ rows, loading, error }) => {
  const [sortField, setSortField] = useState<string>('scheme_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'scheme_name' || field === 'display_name' ? 'asc' : 'desc');
    }
  };

  const sorted = useMemo(() => {
    const pick = (row: SchemeCashBucket): string | number => {
      if (sortField === 'scheme_name') return (row.scheme_name || '').toLowerCase();
      if (sortField === 'display_name') return (row.display_name || '').toLowerCase();
      if (sortField === 'aggr_pct') return Number(row.aggr_pct) || 0;
      return Number(row.buckets?.[sortField as Bucket]) || 0;
    };
    return [...rows].sort((a, b) => {
      const valA = pick(a);
      const valB = pick(b);
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortField, sortDir]);

  return (
    <DataTable<SchemeCashBucket>
      embedded
      data={sorted}
      columns={columns}
      rowKey={row => row.scheme_id}
      totalCount={sorted.length}
      loading={loading}
      error={error}
      currentPage={1}
      totalPages={1}
      onPageChange={() => undefined}
      pageSize={sorted.length || 1}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      hideSearch
      compact
      loadingMessage="Loading scheme cash…"
      emptyMessage="No scheme cash data found."
    />
  );
};

const SchemeCash: React.FC = () => {
  const [cashData, setCashData] = useState<SchemeCashData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSchemeCashData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ordersApi.getSchemeCash();
      setCashData(response);
    } catch (err) {
      const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(apiErr?.response?.data?.error || apiErr?.message || 'Failed to load scheme cash data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchemeCashData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Scheme Cash"
          subtitle="Clients per scheme, bucketed by how much of their AUM is available as cash."
          asOf={cashData?.holding_date ? formatDate(cashData.holding_date) : undefined}
          actions={
            <Button onClick={loadSchemeCashData} disabled={loading}>
              {loading ? 'Loading…' : 'Refresh'}
            </Button>
          }
        />
      }
    >
      <Note tone="info">
        This data assumes 100% of buy orders are executed at the limit price and does not
        include cash generated from today&apos;s sales.
      </Note>

      <Surface title="Instant Cash" meta="Cash available for purchasing stock today">
        <BucketTable rows={cashData?.instant_cash ?? []} loading={loading} error={error} />
      </Surface>

      <Surface title="Cash + Liquid" meta="Cash and cash equivalents available in the account">
        <BucketTable rows={cashData?.cash_plus_liquid ?? []} loading={loading} error={error} />
      </Surface>
    </PageFrame>
  );
};

export default SchemeCash;
